import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { icons } from "./icons";

/** Fixed tone → meaning map (Foundation DESIGN.md). Don't pick a tone by its colour. */
export type StatusTone = "success" | "warning" | "pending" | "info" | "neutral" | "destructive";

/**
 * THE status chip. Always a label (colour never carries meaning alone). `quiet` is the outline
 * register for the unremarkable case (active, passing) so it doesn't shout over real problems.
 */
export function StatusChip({ tone, children, quiet }: { tone: StatusTone; children: ReactNode; quiet?: boolean }) {
  return <span className={`chip chip--${tone}${quiet ? " chip--quiet" : ""}`}>{children}</span>;
}

export function EmptyState({ title, description, action, icon = icons.inbox }: { title: string; description?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty__icon">{icon}</span>
      <p className="empty__t">{title}</p>
      {description && <p className="empty__d">{description}</p>}
      {action}
    </div>
  );
}

/** A banner in the page flow. Errors are announced as alerts, the rest politely. */
export function InlineAlert({ variant, title, children }: { variant: "info" | "warning" | "error" | "success"; title?: string; children?: ReactNode }) {
  const icon = variant === "success" ? icons.check : variant === "info" ? icons.info : icons.warning;
  return (
    <div className={`alert alert--${variant}`} role={variant === "error" ? "alert" : "status"}>
      {icon}
      <div>
        {title && <p className="alert__t">{title}</p>}
        {children && <div className="alert__d">{children}</div>}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- toasts */

/** An optional action on a toast, such as Undo; selecting it runs it and closes the toast. */
export interface ToastAction { label: string; onSelect: () => void }
interface Toast { id: number; text: string; tone: "default" | "error"; action?: ToastAction }
const ToastContext = createContext<(text: string, tone?: Toast["tone"], action?: ToastAction) => void>(() => undefined);

/** Brief confirmations ("Invitation sent."), for 6 seconds, with an optional action such as Undo. Errors that need action belong in an InlineAlert. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setToasts(t => t.filter(x => x.id !== id)), []);
  const show = useCallback((text: string, tone: Toast["tone"] = "default", action?: ToastAction) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t.slice(-2), { id, text, tone, action }]);
    setTimeout(() => dismiss(id), 6000);
  }, [dismiss]);
  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map(t => (
          <div key={t.id} role="status" className={`toast${t.tone === "error" ? " toast--error" : ""}`}>
            <span>{t.text}</span>
            {t.action && <button type="button" className="toast__a" onClick={() => { dismiss(t.id); t.action!.onSelect(); }}>{t.action.label}</button>}
            <button type="button" className="toast__x" aria-label="Dismiss" onClick={() => dismiss(t.id)}>×</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
